import { API } from 'aws-amplify';
import { Auth } from 'aws-amplify/lib';
import axios from 'axios';

import config from '../config/aws';
import { withCorrelationRetry, withInitHeaders } from './correlationIdInitiated';
import { ApiResponse } from './types';

const isLocal = (endpoint: string) =>
    ['/localhost', '/127.0.0.1'].reduce((prev, curr) => prev || endpoint.indexOf(curr) > -1, false);

const getServiceEndpoint = (api: string) => {
    const service = config.apiGatewayEndpoints.find((a) => a.name === api);
    if (!service) {
        throw new Error(`unrecognized service name: ${api}`);
    }
    return service;
};

export const getApi = async <T>(api: string, path: string, args = '') => {
    const endpoint = getServiceEndpoint(api).endpoint;
    const fullPath = args ? `${path}?${args}` : path;
    if (isLocal(endpoint)) {
        return withCorrelationRetry(
            endpoint,
            async (initiated) => {
                const response = await axios.get(endpoint + fullPath, {
                    headers: { ...(await getLocalHeaders()), ...initiated },
                });
                return response.data as T;
            },
            { canRetry: true }
        );
    }
    const response = await withCorrelationRetry(
        endpoint,
        (initiated) => API.get(api, fullPath, withInitHeaders({}, initiated)),
        { canRetry: true }
    );
    const typed = response as ApiResponse<T>;
    if (!typed.ok) {
        throw typed.error;
    }
    return typed.data;
};

export const putApi = async (api: string, path: string, data: unknown) => {
    const endpoint = getServiceEndpoint(api).endpoint;
    if (isLocal(endpoint)) {
        return withCorrelationRetry(endpoint, async (initiated) => {
            const response = await axios.put(endpoint + path, data, {
                headers: { ...(await getLocalHeaders()), ...initiated },
            });
            return response.data;
        });
    }
    return withCorrelationRetry(endpoint, (initiated) =>
        API.put(api, path, withInitHeaders({ body: data }, initiated))
    );
};

export const postApi = async (api: string, path: string, data: unknown) => {
    const endpoint = getServiceEndpoint(api).endpoint;
    if (isLocal(endpoint)) {
        return withCorrelationRetry(endpoint, async (initiated) => {
            const response = await axios.post(endpoint + path, data, {
                headers: { ...(await getLocalHeaders()), ...initiated },
            });
            return response.data;
        });
    }
    return withCorrelationRetry(endpoint, (initiated) =>
        API.post(api, path, withInitHeaders({ body: data }, initiated))
    );
};

export const deleteApi = async (api: string, path: string) => {
    const endpoint = getServiceEndpoint(api).endpoint;
    if (isLocal(endpoint)) {
        return withCorrelationRetry(endpoint, async (initiated) => {
            const response = await axios.delete(endpoint + path, {
                headers: { ...(await getLocalHeaders()), ...initiated },
            });
            return response.data;
        });
    }
    return withCorrelationRetry(endpoint, (initiated) => API.del(api, path, withInitHeaders({}, initiated)));
};

// TODO delete this...
const identityPoolFromUserPool: { [key: string]: string } = {
    'eu-west-1_Db4DM5HT6': 'eu-west-1:6bbb94ef-3c50-46ca-87ad-3bdec2f94b07', // Dev member
    'eu-west-1_DL7FquCz9': 'eu-west-1:d28b3594-0197-4aae-87a8-1d891fb82fa0', // Dev expert
    'eu-west-1_QTVBsPDPs': 'eu-west-1:bd38bf12-1fd9-4728-a9e2-4e383c758248', // Dev partner
    'eu-west-1_GP0YnPgVe': 'eu-west-1:1bcafe7b-2f17-4204-91be-a6b5efcebca1', // Test member
    'eu-west-1_zCRwzrwHT': 'eu-west-1:3f13d29d-7c6c-4b48-bb88-bd8b1bf03ce9', // Test expert
    'eu-west-1_Owth3qCIL': 'eu-west-1:3fcb90a9-b34b-4407-b010-dfe9f0a2a18d', // Test partner
};

// TODO this is a copy of the code used in apiCallDispatcher.js
export const getLocalHeaders = async () => {
    const cognitoIdentityCredentials = await Auth.currentCredentials();
    const userId = cognitoIdentityCredentials.identityId.split(':')[1];

    const authedUser = await Auth.currentAuthenticatedUser();

    // TODO this is a sure sign that we are relying on the wrong cognito info at present for auth, and we really need
    // to move to something more reliable like JWTs...
    const cognitoPoolId = identityPoolFromUserPool[authedUser?.pool?.userPoolId];
    const role = 'None'; // The partner cognito pool doesn't seem to adopt a role in the same way as experts do...
    const userArn = `arn:aws:sts::0000000000:assumed-role/${role}/CognitoIdentityCredentials`;
    const cognitoId = cognitoIdentityCredentials.identityId;

    return {
        role: role,
        cognitoId,
        'content-type': 'application/json',
        'User-Arn': userArn,
        'Cognito-Identity-Pool-Id': cognitoPoolId,
        'Cognito-Identity-Id': userId,
    };
};
